import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';

import { arrayIsTruthy, getProperty } from '@shared-helpers/array-object-helpers';
import { addDecimals } from '@shared-helpers/string-number-helpers';

import { ITableConfigEntity, ITextClickEvent } from '../../interfaces/internal-interfaces';
import { DatahubService } from '../../services/datahub.service';

@Component({
  selector: 'table-like-layout',
  templateUrl: './table-like-layout.component.html',
  styleUrl: './table-like-layout.component.css',
  standalone: false,
})
export class TableLikeLayoutComponent<
    TEntity extends ITableEntity,
    TRow extends ITableRow<Extract<keyof TEntity, string>>,
    TConfig extends ITableConfigEntity<Extract<keyof TEntity, string>>,
  >
  implements OnInit, OnChanges, OnDestroy
{
  private hasNoValues = false;

  private highlight: ITableLikeLayoutHighlight = {
    shouldShow: false,
    entity: null,
    row: null,
    column: null,
  };

  public total: TEntity[] = [];

  private recalcSubscriber$ = new Subscription();
  private unfocusSubscriber$ = new Subscription();
  public columns = 0;
  public isBars = false;
  public contentRowClasses: string[] = [];
  public rowHeight = '10';
  public rowSpanMultiplier = 4; //the rowHeight is multiplied by this number
  public headerHeight = '50';
  public totalRowHeight = '15';
  public isSimple = false;

  @Input() public config: TConfig[] = [];
  @Input() public shouldHideHeaders = false;
  @Input() public values: any[] = []; //should be TEntity[] !! - The values array should be an object array. It should contain a property called "shouldShow" to hide it when filtering ect.
  @Input() public columnLimit = '';
  @Input() public valuePath?: string; //in the object array, this string points to the values you want to loop over.
  @Input() public configPath?: string; //in the object array, this string points to the config. This is useful when config is unique on a row by row basis.
  @Input() public shouldHideTotal = false;
  @Input() public appearance?: ITableLikeLayoutAppearanceOptions = 'bars';
  @Input() public labelClass = '';
  @Input() public containerClass = '';
  @Input() public additionalContentRowClasses: string[] = [];
  @Input() public omitActivityCodesFromTotalRow: string[] = [];

  @Output() public readonly rowSelected = new EventEmitter<number>();
  @Output() public readonly numberBoxClicked = new EventEmitter<INumberBoxClickedEvent<TEntity, any>>();
  @Output() public readonly cellClicked = new EventEmitter<TConfig>();
  @Output() public readonly headerClicked = new EventEmitter<TConfig>();
  @Output() public readonly valueChanged = new EventEmitter<ITableLikeLayoutEvent<TEntity>>();
  @Output() public readonly textClicked = new EventEmitter<ITextClickEvent>();
  @Output() public readonly iconClicked = new EventEmitter<ITableLikeLayoutEvent<TEntity>>();
  @Output() public readonly plainButtonClicked = new EventEmitter<ITableLikeLayoutEvent<TEntity>>();

  constructor(private readonly datahub: DatahubService) {}

  public ngOnChanges(): void {
    this.columns = this.determineColumns();
    this.hasNoValues = !arrayIsTruthy(this.values);
    this.isBars = this.appearance === 'bars';
    this.isSimple = !this.valuePath;
    if (this.appearance === 'attachment') this.contentRowClasses = ['attachments'];
    if (this.isBars) this.contentRowClasses = ['spacing', 'shaded', 'mat-elevation-z2'];
    if ((!this.isBars && this.appearance === 'flat') || this.appearance === 'timeEval') {
      this.rowHeight = '1em';
      this.rowSpanMultiplier = 5; //The row height is multiped by this.
      this.headerHeight = '35';
    }
    if (!this.isBars && this.appearance === 'summary') {
      this.rowHeight = '40';
      this.rowSpanMultiplier = 1; //The row height is multiped by this. We want rows to be an exact match in this situation so we keep it at 1
      this.headerHeight = '35';
    }

    //Add the extra classes to the existing classes
    this.contentRowClasses = this.contentRowClasses.concat(this.additionalContentRowClasses);
  }

  public ngOnInit(): void {
    this.checkRequired();
    if (this.appearance == null) this.appearance = 'bars'; //allow undefined to be passed in, but always default to this if that happens
    this.recalcSubscriber$ = this.datahub.recalcTotals$.subscribe({
      next: (shouldCalc) => {
        if (shouldCalc) {
          this.calcTotals();
        }
      },
      error: (err) => {
        console.error('Error in recalcTotals$ subscription:', err);
      },
    });
    this.unfocusSubscriber$ = this.datahub.unfocusTable$.subscribe({
      next: () => {
        this.hideHighlight();
      },
      error: (err) => {
        console.error('Error in unfocusTable$ subscription:', err);
      },
    });
    this.columns = this.determineColumns();
  }

  public ngOnDestroy(): void {
    this.recalcSubscriber$.unsubscribe();
    this.unfocusSubscriber$.unsubscribe();
  }

  private determineColumns(): number {
    if (this.columnLimit) return Number(this.columnLimit);
    else return this.reduceObjArrayToSum(this.config, 'columns');
  }

  public handleChange(value: any, columnConfig: TConfig, row: number, subRow: number, column: number): void {
    const entity = this.values[row];
    const emitter: ITableLikeLayoutEvent<TEntity> = { entity, columnConfig, row, subRow, column };
    if (columnConfig.nestedObjectName) {
      entity[columnConfig.nestedObjectName][columnConfig.key] = value;
    } else {
      entity[columnConfig.key] = value;
    }
    this.valueChanged.emit(emitter);
  }

  private calcTotals(): void {
    if (!this.shouldHideTotal) this.updateTotalRow();
  }

  private updateTotalRow(): void {
    //NOTE: Table totals are kind of a weird and loose type that might be an object or it might be an array. it just depends on the data being used
    if (this.hasNoValues) return;
    const totalData: Record<string, any> = {};
    this.config.forEach((column) => {
      if (column.shouldTotal) {
        if (column.nestedObjectName) {
          if (!totalData[column.nestedObjectName]) totalData[column.nestedObjectName] = {};
          if (!totalData[column.nestedObjectName][column.key]) totalData[column.nestedObjectName][column.key] = '0';
        } else {
          totalData[column.key] = '0';
        }
      } else {
        totalData[column.key] = '';
        return;
      }

      this.values.forEach((entity: TEntity) => {
        if (!entity.shouldShow) return;

        this.findValuesForList<ITableTotalRow>(entity).forEach((row, index) => {
          if (index > 0 && column.shouldSpanAllRows) return;
          //Exclude any activity codes we explicity omit. An example would be to not include Per Diems in the total so that we don't add hours and dollars together
          if (!this.omitActivityCodesFromTotalRow.includes(row.activityNumber)) {
            const valToAdd = this.findTotalValue(row, column);
            if (column.nestedObjectName) {
              totalData[column.nestedObjectName][column.key] = this.addComplex(
                totalData[column.nestedObjectName][column.key] as ITotalForTable,
                valToAdd,
              );
            } else {
              totalData[column.key] = this.addComplex(totalData[column.key], valToAdd);
            }
          }
        });
      });
    });

    let totalObj: Array<typeof totalData> | Record<string, unknown>;
    if (this.isSimple) {
      totalObj = [totalData];
    } else {
      totalObj = {
        shouldShow: true,
        isTotalRow: true,
      };
      totalObj[this.valuePath] = [totalData];
    }
    this.total[0] = totalObj as TEntity; //force it back to the generic type for this table
  }

  public findTotalValue(totalRow: ITableTotalRow, totalColumn: TConfig): ITotalForTable {
    let finalVal: ITotalForTable | null = '';
    if (totalColumn.shouldTotal) {
      if (totalColumn.nestedObjectName) {
        finalVal = getProperty(totalRow, `${totalColumn.nestedObjectName}.${totalColumn.key}`);
      } else {
        finalVal = totalRow[totalColumn.key];
      }
      if (finalVal === '' || finalVal == null) finalVal = '0';
    }

    return finalVal;
  }

  private addComplex(receiver: ITotalForTable, sender: ITotalForTable): ITotalForTable {
    //If we are given numbers or strings, add them together
    if (typeof receiver !== 'object' && typeof sender !== 'object') {
      return addDecimals(receiver, sender);
    }

    //If we are given an object with numbers as values, total them up by object keys
    const returnObj = typeof receiver === 'string' ? {} : receiver;
    if (typeof sender !== 'string') {
      Object.keys(sender).forEach((key) => {
        if (!returnObj[key]) returnObj[key] = '0';
        returnObj[key] = addDecimals(returnObj[key], sender[key]);
      });
    }
    return returnObj;
  }

  private checkRequired(): void {
    if (!this.config) throw new Error('config is required. config values can be found in config.ts');
    if (!this.values) throw new Error('Values are required. Values should map to config');
  }

  public handleNumberBoxClick(
    entity: TEntity,
    columnConfig: TConfig,
    entityIndex: number,
    rowIndex: number,
    columnIndex: number,
  ): void {
    this.datahub.unfocusTable();
    this.changeHighlight(entityIndex, rowIndex, columnIndex);
    this.numberBoxClicked.emit({
      entity,
      row: rowIndex,
      column: columnConfig,
    });
  }

  public handleHeaderClicked(config: TConfig): void {
    if (config.shouldDetectHeaderClick) {
      this.headerClicked.emit(config);
    }
  }

  public handleTextClicked(
    entity: TEntity,
    config: TConfig,
    value: undefined | number | boolean | ITableConfigEntity[],
    entityIndex: number,
    rowIndex: number,
    columnIndex: number,
  ): void {
    if (!config.shouldDetectClick) return;
    const stringifiedValue = value === undefined || Array.isArray(value) ? '' : value.toString();
    this.textClicked.emit({ entity, config, value: stringifiedValue });
    this.changeHighlight(entityIndex, rowIndex, columnIndex);
  }

  public handleIconClicked(entity: TEntity, columnConfig: TConfig, row: number, subRow: number, column: number): void {
    const emitter: ITableLikeLayoutEvent<TEntity> = { entity, columnConfig, row, subRow, column };
    this.iconClicked.emit(emitter);
  }

  public handlePlainButtonClicked(entity: TEntity, columnConfig: TConfig, row: number, subRow: number, column: number): void {
    const emitter: ITableLikeLayoutEvent<TEntity> = { entity, columnConfig, row, subRow, column };
    this.plainButtonClicked.emit(emitter);
  }

  private changeHighlight(entity: number, row: number, column: number): void {
    this.highlight = { shouldShow: true, entity, row, column };
  }

  private hideHighlight(): void {
    this.changeHighlight(-1, -1, -1);
  }

  public shouldHighlight(entityIndex: number, rowIndex: number, columnIndex: number): boolean {
    if (this.highlight.entity !== entityIndex) return false;
    if (this.highlight.row !== rowIndex) return false;
    if (this.highlight.column !== columnIndex) return false;
    return true;
  }

  public findRowSpan(columnConfig: TConfig, entity: TEntity, row: TRow): number {
    if (row.rowSpanOverride) return row.rowSpanOverride; //if we need to span a custom number of rows, do that
    if (!columnConfig.shouldSpanAllRows) return this.rowSpanMultiplier;
    const valueArray = getProperty(entity, this.valuePath);
    if (arrayIsTruthy(valueArray)) {
      //If the value contains any rows that need to override the number of rows to span, we want to subtract that from our calculations
      const customRowSpans = valueArray.filter((x) => x.rowSpanOverride);
      return (valueArray.length - customRowSpans.length) * this.rowSpanMultiplier;
    }
    return this.values.length * this.rowSpanMultiplier;
  }

  public findValuesForList<T = TRow>(entity: TEntity): Array<T> {
    const values = getProperty(entity, this.valuePath);
    if (arrayIsTruthy(values)) return values;
    else return [values];
  }

  public findValueForGrid(row: TRow, column: TConfig) {
    if (column.nestedObjectName) return getProperty(row, `${column.nestedObjectName}.${column.key}`);
    else return getProperty(row, column.key);
  }

  public findDisplay(row: TRow, column: TConfig): string {
    const key = column.displayKey ?? column.key;

    if (column.nestedObjectName) return getProperty(row, `${column.nestedObjectName}.${key}`);
    else return getProperty(row, key);
  }

  public findColumnConfigs(entity: TEntity, row: TRow) {
    //First, if the row contains a special config to override with, using this takes priority
    if (row.configOverride) return row.configOverride;

    //Next, we look for a config object on the entity. If that does NOT exist we use the default config on this component
    if (!this.configPath || !getProperty(entity, this.configPath, false)) return this.config;

    //Lastly, if there is no config path specified, or the path whiffs, use the default component config.
    return entity[this.configPath]; //This is a string-index property name lookup on a class. At this point we have to hope everything was spelled right and uses the correct types!
  }

  private reduceObjArrayToSum<T>(array: T[], propertyName: keyof T): number {
    //TODO: Use the real JS array.reduce() method here instead of rolling a custom one
    let sum = 0;
    array.forEach((element) => {
      sum += Number(element[propertyName]);
    });
    return sum;
  }
}

interface ITableEntity {
  [key: string]: any;
  tooltip?: string;
  shouldShow?: boolean;
  shouldFlag?: boolean;
  shouldNeverShow?: boolean;
  shouldHighlight?: boolean;
}

interface ITableTotalRow {
  [key: string]: undefined | string;
  //These properties might be on some employee-specific objects. Not ideal to have this non-generic stuff in here!
  activityNumber?: string;
}

interface ITableRow<T> {
  [key: string]: undefined | number | boolean | ITableConfigEntity<T>[];
  rowSpanOverride?: number;
  configOverride?: ITableConfigEntity<T>[];
  canEdit?: boolean;
}

interface ITableLikeLayoutHighlight {
  shouldShow: boolean;
  entity: number | null;
  row: number | null;
  column: number | null;
}

export interface ITableLikeLayoutEvent<T, K = string> {
  entity: T;
  columnConfig: ITableConfigEntity<K>;
  row: number;
  column: number;
  subRow: number;
}

export interface INumberBoxClickedEvent<T, K> {
  entity: T;
  row: number;
  column: ITableConfigEntity<K>;
}

type ITotalForTable = string | Record<string, string>;

export type ITableLikeLayoutAppearanceOptions = 'bars' | 'flat' | 'summary' | 'attachment' | 'timeEval';
